import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import styled from 'styled-components'

import { Button } from 'components/Button/Button'
import InputCard from 'components/Form/InputCard/InputCard'
import { addToast } from 'components/Toast/Toast'
import { Modal } from 'components-ref/molecules/modal/modal'
import {
  createSubscriptionList,
  updateSubscriptionList
} from 'services/endpoints/subscriptionList'
import { useAuthStore } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { useSubscriptionListsCacheStore } from 'store/subscriptionListsCacheStore'

interface CreateListModalProps {
  listNames?: string[]
  onClose: () => void
  setLists?: any
  lists?: any
  listId?: string
  listChangeName?: string
}

export const CreateListModal = ({
  onClose,
  setLists,
  lists,
  listId,
  listChangeName
}: CreateListModalProps) => {
  const [listName, setListName] = useState('')
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  useEffect(() => {
    if (listChangeName) {
      setListName(listChangeName)
    }
  }, [listChangeName])

  const patchSelectedListsCache = (subscriptionList: any) => {
    const me = useAuthStore.getState().userProfile
    if (!me || !subscriptionList?.id) return

    const current = Array.isArray(me.selected_lists) ? me.selected_lists : []

    const updatedSelectedLists = [
      ...current.filter((list: any) => list.id !== subscriptionList.id),
      {
        id: subscriptionList.id,
        name: subscriptionList.name,
        selected: subscriptionList.selected,
        order: subscriptionList.order,
        default: subscriptionList.default
      }
    ].sort((a: any, b: any) => Number(a.order ?? 999) - Number(b.order ?? 999))

    const updatedProfile = {
      ...me,
      selected_lists: updatedSelectedLists
    }

    useAuthStore.getState().setUserProfile(updatedProfile as any)

    useProfileCacheStore.getState().setProfile(
      me.username,
      updatedProfile as any,
      me.id
    )
  }

  const addSubscriptionList = useMutation(
    async (name: string) => await createSubscriptionList(name),
    {
      onSuccess: (res: any) => {
        const created = res?.subscription_list

        if (created?.id && created?.name) {
            useSubscriptionListsCacheStore
            .getState()
            .upsertList({
                id: created.id,
                name: created.name,
                count: 0,
                avatars: []
            })

          setLists?.((prev: any[]) => {
            const base = Array.isArray(prev) ? prev : []

            if (base.some(w => w?.data?.id === created.id)) return base

            return [
              {
                data: {
                  id: created.id,
                  name: created.name,
                  selected: created.selected,
                  order: created.order,
                  slug: created?.slug,
                  is_all: created?.is_all,
                  default: created?.default
                }
              },
              ...base
            ]
          })

          patchSelectedListsCache(created)
        } else {
          console.warn('Create list response missing subscription_list', res)
        }

        onClose()
      },
      onError: () => {
        addToast('error', t('error:anErrorOccuredPleaseTryAgain'))
        onClose()
      }
    }
  )

    const handleEditList = async (id: string) => {
    const name = listName.trim()

    if (!name) {
        addToast('error', t('error:pleaseInputGroupName'))
        return
    }

    try {
        const res = await updateSubscriptionList(id, { name })
        const updated = res?.subscription_list

        const finalName = updated?.name ?? name

        setLists?.((prev: any[]) => {
        const base = Array.isArray(prev) ? prev : lists ?? []

        return base.map((list: any) =>
            list?.data?.id === id || list?.id === id
            ? {
                ...list,
                data: {
                    ...(list.data ?? list),
                    name: finalName,
                    selected: updated?.selected ?? list?.data?.selected,
                    order: updated?.order ?? list?.data?.order
                }
                }
            : list
        )
        })

        useSubscriptionListsCacheStore.getState().upsertList({
        id,
        name: finalName,
        count: 0,
        avatars: []
        })

        const profile = useAuthStore.getState().userProfile

        if (profile) {
        const updatedSelectedLists = (profile.selected_lists ?? []).map((list: any) =>
            list.id === id
            ? {
                ...list,
                name: finalName,
                selected: updated?.selected ?? list.selected,
                order: updated?.order ?? list.order
                }
            : list
        )

        useAuthStore.getState().setUserProfile({
            selected_lists: updatedSelectedLists
        })

        useProfileCacheStore.getState().setProfile(
            profile.username,
            {
            ...profile,
            selected_lists: updatedSelectedLists
            } as any,
            profile.id
        )
        }

        onClose()
    } catch (error) {
        console.error(`Failed to update list with ID ${id}`, error)
        addToast('error', t('error:anErrorOccuredPleaseTryAgain'))
    }
    }

  return (
    <Modal
      title={listId ? t('Edit List Name') : t('createNewList')}
      onClose={onClose}
      hasShadow={true}
      paddingBottom={1}
      maxWidth={40}
    >
      <Container>
        <InputCard
          customClass="addediti_Input_box"
          value={listName}
          changeFn={(val: any) => setListName(val)}
          placeholder={t('listName')}
          hasDesc={true}
          desc={`${listName.trim().length} / 30 ${t('characters')}`}
          maxChars={30}
          type="text"
        />

        <Buttons>
          <CustomButton
            text={t('back')}
            color="transparent"
            type="transparent--black1px"
            clickFn={() => {
              onClose()
              setListName('')
            }}
            height="3"
            width="13"
            font="mont-14-normal"
          />

          {listId ? (
            <CustomButton
              text={t('Edit')}
              color="blue"
              customClass="add-fans-group-modal-button"
              clickFn={() => {
                handleEditList(listId)
              }}
              height="3"
              width="13"
              font="mont-14-normal"
            />
          ) : (
            <CustomButton
              text={t('add')}
              color="blue"
              customClass={`add-fans-group-modal-button ${
                addSubscriptionList.isLoading ? 'add-fans-group-modal-button-loading' : ''
              }`}
              clickFn={() => {
                const name = listName.trim()

                if (!name) {
                  addToast('error', t('error:pleaseInputGroupName'))
                  return
                }

                addSubscriptionList.mutate(name)
              }}
              height="3"
              width="13"
              font="mont-14-normal"
            />
          )}
        </Buttons>
      </Container>
    </Modal>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
`

const Buttons = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`

const CustomButton = styled(Button)`
  margin-left: 1rem;
  cursor: pointer;
  transition: opacity ease-in-out 0.3s;
  user-select: none;

  &.add-fans-group-modal-button-loading {
    cursor: default;
    pointer-events: none;
    opacity: 0.7;
  }
`